/**
 * Analytics Service
 * Lightweight, privacy-first analytics tracking
 * Batches events and respects user consent
 */

import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const API = '/api';  // Use relative URLs

class AnalyticsService {
  constructor() {
    this.eventQueue = [];
    this.sessionId = this.getOrCreateSessionId();
    this.batchSize = 20;
    this.batchInterval = 30000; // 30 seconds
    this.flushTimer = null;
    this.hasConsent = true;
    
    // Check consent on init
    this.checkConsent();
    
    // Start batch timer
    this.startBatchTimer();
    
    // Flush on page unload
    this.setupUnloadHandler();
  }

  getOrCreateSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = uuidv4();
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return this.hashSessionId(sessionId);
  }

  hashSessionId(sessionId) {
    // Simple hash for privacy
    return btoa(sessionId).substring(0, 16);
  }

  async checkConsent() {
    try {
      // Check Do Not Track
      if (navigator.doNotTrack === '1' || window.doNotTrack === '1') {
        this.hasConsent = false;
        return;
      }

      // Check localStorage consent flag
      const consent = localStorage.getItem('analytics_consent');
      if (consent === 'false') {
        this.hasConsent = false;
        return;
      }

      // If logged in, check server-side consent
      const token = localStorage.getItem('token');
      if (token) {
        const response = await axios.get(`${API}/analytics/consent`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        this.hasConsent = response.data.analytics_consent && !response.data.do_not_track;
        
        // Update localStorage
        localStorage.setItem('analytics_consent', this.hasConsent.toString());
      }
    } catch (error) {
      console.error('Error checking analytics consent:', error);
      // Default to consent if check fails (opt-out approach)
      this.hasConsent = true;
    }
  }

  async setConsent(analytics_consent, do_not_track = false) {
    try {
      this.hasConsent = analytics_consent && !do_not_track;
      localStorage.setItem('analytics_consent', this.hasConsent.toString());

      const token = localStorage.getItem('token');
      if (token) {
        await axios.post(`${API}/analytics/consent`, 
          { analytics_consent, do_not_track },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }
    } catch (error) {
      console.error('Error setting analytics consent:', error);
    }
  }

  track(eventType, properties = {}) {
    // Skip if no consent
    if (!this.hasConsent) {
      return;
    }

    const event = {
      event_id: uuidv4(),
      event_type: eventType,
      session_id_hash: this.sessionId,
      timestamp: new Date().toISOString(),
      properties,
      has_consent: true
    };

    this.eventQueue.push(event);

    // Flush if batch size reached
    if (this.eventQueue.length >= this.batchSize) {
      this.flush();
    }
  }

  async flush() {
    if (this.eventQueue.length === 0 || !this.hasConsent) {
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      await axios.post(`${API}/analytics/track`, {
        events,
        client_timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error sending analytics:', error);
      // Don't re-queue events to avoid infinite loops
    }
  }

  startBatchTimer() {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.batchInterval);
  }

  setupUnloadHandler() {
    // Flush on page visibility change
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flush();
      }
    });

    // Flush on beforeunload
    window.addEventListener('beforeunload', () => {
      this.flush();
    });
  }

  // Convenience methods for common events

  trackFeedImpression(postId, contentType, genres, tab, position) {
    this.track('feed_impression', {
      post_id: postId,
      content_type: contentType,
      genres,
      tab,
      position
    });
  }

  trackFeedClick(postId, destination) {
    this.track('feed_click', {
      post_id: postId,
      destination
    });
  }

  trackEpisodeViewStart(seriesId, episodeId, genres, source) {
    this.track('episode_view_start', {
      series_id: seriesId,
      episode_id: episodeId,
      genres,
      source
    });
  }

  trackEpisodeViewComplete(seriesId, episodeId, readTimeMs) {
    this.track('episode_view_complete', {
      series_id: seriesId,
      episode_id: episodeId,
      read_time_ms: readTimeMs
    });
  }

  trackReaction(targetType, targetId, reaction, action = 'add') {
    this.track(action === 'add' ? 'reaction_add' : 'reaction_remove', {
      target_type: targetType,
      target_id: targetId,
      reaction
    });
  }

  trackFollow(creatorId, action = 'follow') {
    this.track(action === 'follow' ? 'follow_creator' : 'unfollow_creator', {
      creator_id: creatorId
    });
  }

  trackOnboardingGenreSet(genres, contentTypes) {
    this.track('onboarding_genre_set', {
      genres,
      content_types: contentTypes
    });
  }

  trackFilterChange(genres, contentTypes, tab) {
    this.track('filter_change', {
      genres,
      content_types: contentTypes,
      tab
    });
  }

  trackSearch(queryLen, filtersApplied) {
    this.track('search_performed', {
      query_len: queryLen,
      filters_applied: filtersApplied
    });
  }

  trackCreatorUpload(contentType, genres) {
    this.track('creator_upload', {
      content_type: contentType,
      genres
    });
  }

  trackTip(creatorId, amountCents, completed = false) {
    this.track(completed ? 'tip_completed' : 'tip_initiated', {
      creator_id: creatorId,
      amount_cents: amountCents
    });
  }

  trackSubscription(creatorId, tier, action = 'start') {
    this.track(action === 'start' ? 'subscription_start' : 'subscription_cancel', {
      creator_id: creatorId,
      tier
    });
  }

  trackComment(targetType, targetId) {
    this.track('comment_add', {
      target_type: targetType,
      target_id: targetId
    });
  }

  trackFanWallPost(hasImage) {
    this.track('fanwall_post_create', {
      has_image: hasImage
    });
  }
}

// Create singleton instance
const analytics = new AnalyticsService();

export default analytics;
